import type { ReactNode } from "react"
import { useLocation } from "react-router"
import { FondoPuerto } from "@/componentes/acceso/FondoPuerto"
import { usarSesion } from "@/lib/sesion"

/**
 * El fondo del sistema entero: el puerto en video, vivo en el acceso y
 * apagado y a media velocidad una vez dentro.
 *
 * ⚠ **Vive POR ENCIMA del enrutador, y ahí está todo el truco.** Si el telón se
 * montara dentro de la pantalla de acceso, entrar lo desmontaría: el video
 * parpadearía a negro y volvería a cargar del otro lado, que es justo lo que
 * rompería la ilusión de que no se ha cambiado de pantalla. Montado aquí, entrar
 * no lo toca — sólo le cambia un `prop`.
 *
 * ⚠ **Las rutas públicas NO llevan fondo, y SE DESPLAZAN.** `/t/` (tarea), `/p/`
 * (orden o pago) y `/r/` (reporte) las abre alguien con un enlace de un mensaje de
 * Telegram, sin sesión y sin menú: son una hoja suelta, no una pantalla del sistema.
 * Hasta el 12/10 sólo `/t/` estaba en la lista: la orden y el reporte se pintaban
 * encima del video —ilegibles— y, como esta caja tiene alto fijo y `overflow-hidden`
 * para el armazón, un reporte largo no se podía recorrer. Una ruta pública nueva se
 * añade a `RUTAS_PUBLICAS`.
 */
// `/s/`: los pagos programados de la semana; `/d/`: la disponibilidad bancaria; `/e/`: los
// expedientes abiertos por estatus; `/g/`: el reporte semanal de gastos (13/10).
const RUTAS_PUBLICAS = ["/t/", "/p/", "/i/", "/r/", "/s/", "/d/", "/e/", "/g/"]

export function Escenario({ children }: { children: ReactNode }) {
  const usuario = usarSesion((e) => e.usuario)
  const tema = usarSesion((e) => e.tema)
  const ubicacion = useLocation()

  // Se compara el prefijo y no la ruta entera porque lleva el token detrás.
  const publica = RUTAS_PUBLICAS.some((p) => ubicacion.pathname.startsWith(p))
  // ⚠ **En el modo claro no hay fotografía detrás, ni atenuada.** No es un velo
  // blanco encima: el telón no se monta, y lo que se ve es el fondo del tema. Sólo
  // con sesión: el acceso lleva su foto en cualquier caso.
  const sinFotos = publica || (Boolean(usuario) && tema === "claro")

  return (
    <div className="caja-de-pantalla relative h-dvh overflow-hidden">
      {!sinFotos && <FondoPuerto atenuado={Boolean(usuario)} />}
      {/* `relative` para que quede por encima del telón, que es `absolute`.
          `zona-segura` aparta del notch y de la barra de gestos lo que se lee,
          y la fotografía de detrás sigue llegando hasta el borde. */}
      {publica ? (
        // Superficie opaca del tema y desplazamiento propio: la hoja se lee entera.
        <div
          className="caja-de-pantalla zona-segura relative h-full overflow-y-auto"
          style={{ background: "var(--background)", color: "var(--fg)" }}
        >
          {children}
        </div>
      ) : (
        <div className="caja-de-pantalla zona-segura relative h-full">{children}</div>
      )}
    </div>
  )
}
